/**
 * Application Constants
 * All configuration values and constants are centralized here for easy maintenance
 * This eliminates hardcoded values scattered across JavaScript files
 */

// ===== OIDC/Authentication Constants =====
// No caching - generate OIDC Authority fresh every time to avoid cache issues

/**
 * Determines OIDC Authority URL based on current domain (no caching)
 * Generates fresh URL every time to avoid cache-related issues
 * Example: diligentcommunityaddon.diligentoneplatform-dev.com -> https://oidc.diligentoneplatform-dev.com
 * @returns {string} - The OIDC authority URL
 */
const determineOidcAuthority = () => {
    try {
        // 🚨 CODE UPDATE VERIFICATION LOG - This confirms updated code is running
        console.log(`[OIDC-AUTH] 🆕 CODE UPDATED - ${new Date().toISOString()}`);
        
        const hostname = window.location.hostname;
        console.log(`[OIDC-AUTH] Current hostname: ${hostname}`);
        
        // 🍪 Cookie Reading Detection
        const allCookies = document.cookie;
        console.log(`[OIDC-AUTH] 🍪 Cookies detected: ${allCookies ? 'YES' : 'NO'}`);
        console.log(`[OIDC-AUTH] 🍪 Cookie count: ${allCookies.split(';').filter(c => c.trim()).length}`);
        if (allCookies) {
            console.log(`[OIDC-AUTH] 🍪 Cookie preview: ${allCookies.substring(0, 100)}${allCookies.length > 100 ? '...' : ''}`);
        }
        
        // Split the hostname by dots
        const domainParts = hostname.split('.');
        console.log(`[OIDC-AUTH] Domain parts: ${JSON.stringify(domainParts)}`);
        
        // Check if this is a local development environment
        const isLocalhost = hostname === 'localhost' || 
                           hostname === '127.0.0.1' || 
                           hostname.startsWith('192.168.') ||
                           hostname.startsWith('10.') ||
                           hostname.endsWith('.local') ||
                           hostname.includes('localhost');
        
        if (isLocalhost) {
            console.log(`[OIDC-AUTH] 🏠 Local development environment detected: ${hostname}. Using fallback.`);
            return 'https://oidc.diligentoneplatform-dev.com';
        }
        
        // We need at least 3 parts: subdomain.platform.extension for production
        if (domainParts.length < 3) {
            const error = new Error(`Invalid domain structure: '${hostname}'. Expected format: subdomain.platform.extension`);
            error.code = 'INVALID_DOMAIN_STRUCTURE';
            console.error(`[OIDC-AUTH] ❌ Invalid domain structure:`, error.message);
            throw error;
        }
        
        // Extract platform identifier from second-to-last part (before .com/.org etc)
        // Example: diligentcommunityaddon.diligentoneplatform-dev.com 
        // -> parts: ['diligentcommunityaddon', 'diligentoneplatform-dev', 'com']
        // -> platform: 'diligentoneplatform-dev'
        const platformPart = domainParts[domainParts.length - 2];
        const extension = domainParts[domainParts.length - 1];
        
        console.log(`[OIDC-AUTH] Extracted platform: ${platformPart}`);
        console.log(`[OIDC-AUTH] Domain extension: ${extension}`);
        
        // Construct OIDC authority URL
        const oidcAuthority = `https://oidc.${platformPart}.${extension}`;
        
        console.log(`[OIDC-AUTH] Generated OIDC Authority: ${oidcAuthority}`);
        
        
        console.log(`[OIDC-AUTH] 🔍 Debug verification:`);
        console.log(`[OIDC-AUTH]   - Input hostname: ${hostname}`);
        console.log(`[OIDC-AUTH]   - Platform part: ${platformPart}`);
        console.log(`[OIDC-AUTH]   - Extension: ${extension}`);
        console.log(`[OIDC-AUTH]   - Final URL: ${oidcAuthority}`);
        
        return oidcAuthority;
        
    } catch (error) {
        console.error(`[OIDC-AUTH] Error generating OIDC authority: ${error.message}`);
        // Fallback to default authority
        return 'https://oidc.diligentoneplatform-dev.com';
    }
};

/**
 * Gets the OIDC Authority URL (generated fresh every time)
 * @returns {string} - The dynamically generated OIDC authority URL
 */
export const getOidcAuthority = () => {
    const authority = determineOidcAuthority();
    console.log(`[OIDC-AUTH] 🔄 Fresh OIDC Authority generated: ${authority}`);
    return authority;
};

/**
 * Dynamic OIDC Authority - generates fresh value every time it's accessed
 * This avoids caching issues and always uses current window location
 * Note: This is now a function, not a constant
 */
export const OIDC_AUTHORITY = () => getOidcAuthority();
export const OIDC_CLIENT_ID = 'community';
export const OIDC_SCOPE = 'openid email profile membership';
export const OIDC_RESPONSE_TYPE = 'code';
export const OIDC_RESPONSE_MODE = 'query';
export const PROMPT_SELECT_ACCOUNT = 'select_account';

// ===== API Configuration =====
export const DESKTOP_FALLBACK_URL = 'https://localhost:44394';
export const CALLBACK_PATH = '/login/callback';

// ===== Dialog & UI Constants =====
export const DIALOG_WIDTH = 500;
export const DIALOG_HEIGHT = 700;
export const POPUP_FEATURES = `popup,width=500,height=700,noopener,noreferrer`;

// ===== Timeout Values (in milliseconds) =====
export const AUTH_TIMEOUT = 300000;                    // 5 minutes
export const DIALOG_AUTO_CLOSE_TIMEOUT = 5000;         // 5 seconds  
export const DOUBLE_JUMP_TIMEOUT = 2000;               // 2 seconds
export const TOKEN_EXPIRY_CHECK_INTERVAL = 300000;     // 5 minutes
export const OIDC_CLIENT_LOAD_WAIT = 500;              // 500ms wait for OIDC client
export const CALLBACK_WINDOW_CLOSE_DELAY = 2000;       // 2 seconds
export const ERROR_WINDOW_CLOSE_DELAY = 1000;          // 1 second
export const AUTHENTICATION_TIMEOUT = 5 * 60 * 1000;   // 5 minutes
export const FORCE_CLOSE_DELAY = 500;                  // 500ms

// ===== Storage & Communication Keys =====
export const OFFICE_AUTH_CHANNEL = 'OfficeAuthChannel';
export const USER_LOGGED_IN_KEY = 'IsUserLoggedIn';
export const USER_DATA_KEY = 'UserData';
export const DOUBLE_JUMP_KEY = 'isDoubleJump';

// ===== URL Parameters =====
export const URL_PARAM_ACTION = 'action';
export const URL_PARAM_DOUBLE_JUMP = 'doubleJump';
export const URL_PARAM_CODE = 'code';
export const URL_PARAM_STATE = 'state';
export const URL_PARAM_ERROR = 'error';
export const URL_PARAM_ERROR_DESCRIPTION = 'error_description';

// ===== Action Types =====
export const ACTION_SIGNOUT = 'signout';

// ===== Message Types =====
export const MESSAGE_SIGN_OUT_SUCCESS = 'SignOutSuccess';
export const MESSAGE_FORCE_CLOSE = 'FORCE_CLOSE';
export const MESSAGE_CLOSE = 'close()';
export const ERROR_PREFIX = 'ERROR:';

// ===== Debug & Environment =====
export const DEBUG_AUTH = false;
export const DEBUG_ENVIRONMENT = false;
export const ENABLE_CONSOLE_LOGS = true;
export const FORCE_ENVIRONMENT = 'auto';
export const LOCALHOST_AS_SHAREPOINT = true;

// ===== CSS Classes & IDs =====
export const CSS_CLASS_SPINNER = '.ms-Spinner';
export const DOM_ID_MESSAGE = 'message';
export const DOM_ID_POPUP_BLOCKED = 'popup-blocked';
export const DOM_ID_SUCCESS_MESSAGE = 'success-message';

// ===== Console Log Prefixes =====
export const LOG_PREFIX_LOGIN_CALLBACK = '[LOGIN-CALLBACK]';
export const LOG_PREFIX_DOUBLE_JUMP = '[DOUBLE-JUMP]';
export const LOG_PREFIX_USER_AUTH = '[USER-AUTH]';
export const LOG_PREFIX_SHAREPOINT_DOUBLE_JUMP = '[SHAREPOINT-DOUBLE-JUMP]';

// ===== Error Messages =====
export const ERROR_OIDC_CLIENT_NOT_AVAILABLE = 'OIDC client library not available - CSP may be blocking external scripts';
export const ERROR_AUTHENTICATION_FAILED = 'Failed to process authentication response';
export const ERROR_POPUP_BLOCKED = 'Popup was blocked. Please allow popups for this site.';
export const ERROR_AUTH_TIMEOUT = 'Authentication timeout';

// ===== Success Messages =====
export const SUCCESS_AUTH_SUCCESSFUL = 'Authentication Successful!';
export const SUCCESS_LOGOUT_SUCCESSFUL = 'Logout Successful!';

// ===== UI Text =====
export const UI_TEXT_PROCESSING_AUTH = 'Processing authentication...';
export const UI_TEXT_CLOSING_DIALOGS = 'Closing dialogs...';
export const UI_TEXT_PLEASE_WAIT = 'Please wait while we redirect you for authentication...';
export const UI_TEXT_POPUP_BLOCKED = 'Popup blocked! Please allow popups for this site and try again.';

// ===== Functions to generate dynamic values =====
export const getRedirectUri = () => `${window.location.origin}${CALLBACK_PATH}`;
export const getPopupFeatures = () => {
    const left = (window.screen.width / 2) - (DIALOG_WIDTH / 2);
    const top = (window.screen.height / 2) - (DIALOG_HEIGHT / 2);
    return `${POPUP_FEATURES},left=${left},top=${top}`;
};

/**
 * Test function to verify OIDC authority generation for different domains
 * This is a helper function for testing/debugging purposes
 * @param {string} testHostname - The hostname to test
 * @returns {string} - The generated OIDC authority URL
 */
export const testOidcAuthorityGeneration = (testHostname) => {
    console.log(`[OIDC-TEST] Testing hostname: ${testHostname}`);
    
    const domainParts = testHostname.split('.');
    console.log(`[OIDC-TEST] Domain parts: ${JSON.stringify(domainParts)}`);
    
    // Check if this is a local development environment
    const isLocalhost = testHostname === 'localhost' || 
                       testHostname === '127.0.0.1' || 
                       testHostname.startsWith('192.168.') ||
                       testHostname.startsWith('10.') ||
                       testHostname.endsWith('.local') ||
                       testHostname.includes('localhost');
    
    if (isLocalhost) {
        console.log(`[OIDC-TEST] 🏠 Local development environment detected: ${testHostname}. Using fallback.`);
        return 'https://oidc.diligentoneplatform-dev.com';
    }
    
    // We need at least 3 parts: subdomain.platform.extension for production
    if (domainParts.length < 3) {
        console.warn(`[OIDC-TEST] Invalid domain structure: ${testHostname}. Using fallback.`);
        return 'https://oidc.diligentoneplatform-dev.com';
    }
    
    const platformParts = domainParts.slice(2);
    const platform = platformParts.join('.');
    const oidcAuthority = `https://oidc.${platform}`;
    
    console.log(`[OIDC-TEST] Generated OIDC Authority: ${oidcAuthority}`);
    
    
    return oidcAuthority;
};

// For debugging - Show that OIDC Authority is now dynamic (no caching)
if (typeof window !== 'undefined' && window.location) {
    const timestamp = new Date().toISOString();
    console.log('\n🚨🚨🚨 [UPDATED-CODE] OIDC AUTHORITY LOADED 🚨🚨🚨');
    console.log(`[OIDC-DEBUG] 🆕 Updated Script Version - Dynamic Authority Active`);
    console.log(`[OIDC-DEBUG] 🕐 Script loaded at: ${timestamp}`);
    console.log(`[OIDC-DEBUG] 🎯 OIDC Authority: ${OIDC_AUTHORITY()}`);
    console.log(`[OIDC-DEBUG] 🔄 No caching - generates fresh every time`);
    console.log(`[OIDC-DEBUG] ✅ Using dynamic authority for all requests: ${OIDC_AUTHORITY()}`);
    console.log('🚨🚨🚨 [UPDATED-CODE] Dynamic OIDC Authority Generation Active 🚨🚨🚨\n');
}
